/**
 * @author Geider Arevalo
 * @since v0.0.1
 * @date 22/11/21
 */
import React, {memo, useMemo} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {useTranslation} from 'react-i18next';
import { Card, Statistic, Row, Col, Typography } from 'antd';
import { CaretUpOutlined } from '@ant-design/icons';
// import bg from '@/assets/images/bg-sidebar.svg';
import styles from './index.less';
import {useAuditData} from '@/modules/auditorAudits/hooks/useAuditData';
import {useUser} from '@dofleini/security';
import {AUDIT_RISK_LEVEL} from '@/modules/main/contants/main';
import {roundNumber} from '@/utils/utils';

const HeaderCover = ({countData}) => {
  const {t} = useTranslation('audits');
  const {currentAudit} = useAuditData();
  const { user } = useUser();

  const [percentCompleted, deductionPercent] = useMemo(() => {
    const deductionPercent = currentAudit?.audit?.deductions?.sort((x, y) => y?.percentage - x?.percentage)?.[0]?.percentage;
    return [
      Number(roundNumber((currentAudit?.finalScore * 100 / currentAudit?.totalScore || 0) - (deductionPercent || 0), 2)),
      deductionPercent
    ];
  }, [currentAudit?.audit?.deductions, currentAudit?.finalScore, currentAudit?.totalScore]);

  const riskColor = useMemo(() => {
    if (currentAudit?.recalculatedRisk === AUDIT_RISK_LEVEL.HIGH) return '#ea7d7d';
    if (currentAudit?.recalculatedRisk === AUDIT_RISK_LEVEL.MEDIUM) return '#ead86c';
    if (currentAudit?.recalculatedRisk === AUDIT_RISK_LEVEL.LOW) return '#afe051';
  }, [currentAudit?.recalculatedRisk]);

  return (
    <Card
      cover={<div
        className={'w-full h-full relative'}
        style={{
          backgroundSize: 'cover',
          borderRadius: '4px 4px 0 0',
          backgroundImage: 'linear-gradient(to right, #fff, #4db7f9)',

        }}>
        <div className={'h-full p-4'}>
          <span style={{bottom: '2rem'}} className={'absolute top-auto'}>
            <Typography.Title level={5}>{currentAudit?.template?.name}</Typography.Title>
            <div className={'inline-flex'}>
              <span
                className={'mr-2'}
                style={{ color: '#4db7f9' }}
              >{currentAudit?.client?.name}</span>
              {`> ${currentAudit?.establishment?.name}`}
            </div>
          </span>
        </div>
      </div>}
      className={classNames('bg-no-repeat bg-center', styles.cardStyle)}
      bodyStyle={{padding: 0}}
    >
      <Row className={'text-black flex-wrap'}>
        {user?.reportType !== 'issues' && <Col lg={8} md={12} sm={24} className={'p-4 w-full'}
          style={{backgroundColor: '#4db7f9'}}
        >
          <span>{t('InspectionScore')}</span>
          <div className={'flex justify-between mt-2 flex-wrap'}>
            <div className="flex items-center justify-center">
              <span className="mr-2">{t('score')}:</span>
              <Statistic className={'font-bold'} valueStyle={{fontSize: '22px'}} value={percentCompleted} prefix={
                <CaretUpOutlined rotate={currentAudit?.valueGoal > percentCompleted ? 180 : 0}/>
              } suffix={'%'}/>
              {deductionPercent &&
                    <Statistic className="ml-2" valueStyle={{fontSize: '14px', color: '#C60022'}}
                      value={`(-${deductionPercent}%)`}/>}
            </div>
            <div className="flex items-center justify-center">
              <span className="mr-2">{t('valueGoal')}:</span>
              <Statistic className={'font-bold'} valueStyle={{fontSize: '22px'}} value={currentAudit?.valueGoal || 0}
                suffix={'%'}/>
            </div>
          </div>
        </Col>}
        {user?.reportType === 'auditRisks' && <Col lg={3} md={12} sm={24} className={'p-4 w-full'}>
          <span>{t('auditRisk')}</span>
          <Statistic
            className={'mt-2 font-bold'}
            valueStyle={{fontSize: '18px', color: riskColor}}
            value={t(`auditRisks.${currentAudit?.recalculatedRisk}`)}
          />
        </Col>}
        <Col lg={user?.reportType === 'percent' ? 8 : user?.reportType === 'auditRisks' ? 3 : 6} md={12} sm={24} className={'p-4 w-full'}>
          <span>{t('IncidentsFound')}</span>
          <Statistic
            className={'mt-2 font-bold'}
            valueStyle={{fontSize: '18px', color: 'black'}}
            value={currentAudit?.totalIssuesCount || countData?.countIncidences}
          />
        </Col>
        {user?.reportType !== 'percent' && <Col lg={user?.reportType === 'auditRisks' ? 2 : 6} md={12} sm={24} className={'p-4 w-full'}>
          <span>{t('IncidentsHighRisk')}</span>
          <Statistic
            className={'mt-2 font-bold'}
            valueStyle={{fontSize: '18px', color: '#EF0000'}}
            value={currentAudit?.highRiskIssuesCount + currentAudit?.ieIssuesCount}
          />
        </Col>}
        {user?.reportType !== 'percent' && <Col lg={user?.reportType === 'auditRisks' ? 2 : 6} md={12} sm={24} className={'p-4 w-full'}>
          <span>{t('IncidentsMedium')}</span>
          <Statistic
            className={'mt-2 font-bold'}
            valueStyle={{fontSize: '18px', color: '#DB6307'}}
            value={currentAudit?.mediumIssuesCount}
          />
        </Col>}
        {user?.reportType !== 'percent' && <Col lg={user?.reportType === 'auditRisks' ? 2 : 6} md={12} sm={24} className={'p-4 w-full'}>
          <span>{t('IncidentsLow')}</span>
          <Statistic
            className={'mt-2 font-bold'}
            valueStyle={{fontSize: '18px', color: '#dbaa07'}}
            value={currentAudit?.lowIssuesCount}
          />
        </Col>}
        {user?.reportType !== 'percent' && <Col lg={user?.reportType === 'auditRisks' ? 3 : 6} md={12} sm={24} className={'p-4 w-full'}>
          <span>{t('IncidentsInfra')}</span>
          <Statistic
            className={'mt-2 font-bold'}
            valueStyle={{fontSize: '18px', color: '#4895f3'}}
            value={currentAudit?.infraIssuesCount}
          />
        </Col>}
      </Row>
    </Card>
  );
};

HeaderCover.propTypes = {
  countData: PropTypes.object,
};

export default memo(HeaderCover);
